/**
 * Fiche complète étudiant : identité + paiements + salle brassage.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, User, Building2, GraduationCap, MapPin, Calendar,
  CheckCircle2, XCircle, AlertTriangle, WifiOff, RefreshCw,
} from 'lucide-react';
import { getFiche, FicheEtudiant } from '../services/verifApi';
import StatutPaiements from '../components/StatutPaiements';
import { logVerification } from '../services/sessionApi';

const fmt = (v: string | undefined) => {
  if (!v) return '0';
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return n.toLocaleString('fr-FR');
};

export default function Fiche() {
  const { matricule = '' } = useParams();
  const [searchParams] = useSearchParams();
  const methode = (searchParams.get('m') || 'cache_local') as any;
  const navigate = useNavigate();
  const [data, setData] = useState<FicheEtudiant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getFiche(matricule);
      setData(r);
      // Log audit — non bloquant, ignore les erreurs (offline, etc.)
      if (r.found && r.etudiant) {
        logVerification({
          matricule: r.etudiant.matricule,
          methode: (methode as any) || 'cache_local',
          etudiant_nom: `${r.etudiant.nom} ${r.etudiant.prenoms}`,
          etudiant_classe: r.etudiant.classe,
          etait_a_jour: r.synthese?.acces_resultats ?? null,
          solde: r.synthese?.solde_total_acces,
          mode_acces: r.synthese?.mode_acces,
        }).catch(() => { /* silence : offline peut-être */ });
      }
    } catch (e: any) {
      setError(e?.message || 'Erreur réseau. Vérifie ta connexion.');
    } finally {
      setLoading(false);
    }
  }, [matricule, methode]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <div className="text-sm text-gray-500">Vérification en cours…</div>
          <div className="font-mono text-xs text-gray-400 mt-1">{matricule}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow p-6 max-w-md w-full text-center">
          <WifiOff size={40} className="mx-auto mb-3 text-red-500" />
          <div className="font-semibold text-red-700 mb-1">Erreur de vérification</div>
          <div className="text-sm text-gray-600 mb-4">{error}</div>
          <div className="flex gap-2 justify-center">
            <button
              onClick={load}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
            >
              <RefreshCw size={14} /> Réessayer
            </button>
            <button
              onClick={() => navigate('/verif', { replace: true })}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300"
            >
              Retour
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data?.found) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow p-6 max-w-md w-full text-center">
          <XCircle size={40} className="mx-auto mb-3 text-red-500" />
          <div className="font-semibold text-red-700 mb-1">Étudiant introuvable</div>
          <div className="text-sm text-gray-600 mb-4">
            Aucun étudiant trouvé pour le matricule <b className="font-mono">{matricule}</b>.
            Vérifie le scan ou saisis manuellement.
          </div>
          <button
            onClick={() => navigate('/verif', { replace: true })}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            Nouvelle recherche
          </button>
        </div>
      </div>
    );
  }

  const etu = data.etudiant!;
  const syn = data.synthese!;
  const accesOk = syn.acces_resultats;

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header sticky */}
      <div className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-3xl mx-auto p-3 flex items-center gap-2">
          <button
            onClick={() => navigate('/verif', { replace: true })}
            className="p-2 rounded-lg hover:bg-gray-100"
            aria-label="Retour"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-gray-500">Fiche de vérification</div>
            <div className="font-semibold text-sm truncate">
              {etu.nom} {etu.prenoms}
            </div>
          </div>
          <button
            onClick={load}
            className="p-2 rounded-lg hover:bg-gray-100"
            aria-label="Actualiser"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-3 space-y-3">
        {data._fromCache && (
          <div className="p-2.5 bg-amber-50 border border-amber-300 rounded-lg text-xs text-amber-800 flex items-center gap-2">
            <WifiOff size={14} />
            <span>
              Données hors ligne (mises en cache
              {data._cachedAt ? ` le ${new Date(data._cachedAt).toLocaleString('fr-FR')}` : ''}).
              Reconnecte-toi pour voir la version à jour.
            </span>
          </div>
        )}

        {/* Bandeau statut principal */}
        <div className={`rounded-xl p-4 border-2 ${
          accesOk
            ? 'bg-emerald-50 border-emerald-400'
            : 'bg-red-50 border-red-400'
        }`}>
          <div className="flex items-start gap-3">
            {accesOk
              ? <CheckCircle2 size={40} className="text-emerald-600 flex-shrink-0" />
              : <AlertTriangle size={40} className="text-red-600 flex-shrink-0" />
            }
            <div className="flex-1 min-w-0">
              <div className={`font-bold text-lg ${accesOk ? 'text-emerald-800' : 'text-red-800'}`}>
                {accesOk ? 'À JOUR' : 'NON À JOUR'}
              </div>
              <div className="text-xs uppercase font-semibold opacity-70 mt-0.5">
                Mode : {syn.mode_acces.replace('_', ' ')}
              </div>
              <div className="text-sm mt-2 grid grid-cols-2 gap-2">
                <div>
                  <div className="opacity-70 text-[11px]">Périodes à jour</div>
                  <div className="font-bold">{syn.nb_a_jour} / {syn.nb_periodes_requises}</div>
                </div>
                <div>
                  <div className="opacity-70 text-[11px]">Solde restant</div>
                  <div className="font-bold font-mono">{fmt(syn.solde_total_acces)} F</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Identité */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-3 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
            <div className="flex items-center gap-3">
              {etu.photo_url ? (
                <img
                  src={etu.photo_url}
                  alt={etu.nom}
                  className="w-16 h-16 rounded-lg object-cover border-2 border-white/50"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-white/20 flex items-center justify-center">
                  <User size={28} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-bold text-lg leading-tight">
                  {etu.nom} {etu.prenoms}
                </div>
                <div className="text-xs opacity-90 font-mono mt-0.5">{etu.matricule}</div>
                {!etu.actif && (
                  <div className="inline-block mt-1 px-2 py-0.5 bg-red-500 rounded text-[10px] uppercase font-bold">
                    Inactif
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-3 divide-y divide-gray-100">
            <Row icon={<GraduationCap size={14} />} label="Classe">
              <span className="font-semibold">{etu.classe || '—'}</span>
              <span className="text-xs text-gray-500 ml-2">
                {etu.niveau} · {etu.option || 'Option non renseignée'}
              </span>
            </Row>
            {etu.pole && (
              <Row icon={<Building2 size={14} />} label="Pôle">
                {etu.pole}
                {etu.parcours && <span className="text-xs text-gray-500 ml-2">· {etu.parcours}</span>}
              </Row>
            )}
            {etu.date_naissance && (
              <Row icon={<Calendar size={14} />} label="Né(e) le">
                {new Date(etu.date_naissance).toLocaleDateString('fr-FR')}
                {etu.lieu_naissance && (
                  <span className="text-xs text-gray-500 ml-2">à {etu.lieu_naissance}</span>
                )}
              </Row>
            )}
            {etu.type_formation !== 'FI' && (
              <Row icon={<AlertTriangle size={14} />} label="Type formation">
                <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-semibold">
                  {etu.type_formation === 'CS' ? 'Cours du Soir' : 'Double Diplôme'}
                </span>
              </Row>
            )}
          </div>
        </div>

        {/* Salle d'examen */}
        {data.placement ? (
          <div className="bg-white rounded-xl border-2 border-indigo-300 p-4">
            <div className="flex items-center gap-2 mb-2">
              <MapPin size={16} className="text-indigo-600" />
              <div className="font-semibold text-indigo-900">Salle de composition</div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs text-gray-500 uppercase">Site</div>
                <div className="font-bold text-indigo-900">{data.placement.site}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase">Salle</div>
                <div className="font-bold text-indigo-900">{data.placement.salle}</div>
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-indigo-100">
              Brassage {data.placement.annee_academique} · {data.placement.session_mode}
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm text-gray-500 flex items-center gap-2">
            <MapPin size={14} />
            Aucune salle de composition attribuée pour le moment.
          </div>
        )}

        {/* Paiements période par période */}
        <div>
          <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center justify-between">
            <span>État des paiements</span>
            <span className="text-xs text-gray-500 font-normal">
              Payé annuel : <b className="font-mono">{fmt(syn.total_paye_annuel)} F</b>
            </span>
          </div>
          <StatutPaiements paiements={data.paiements || []} />
        </div>

        <div className="pt-2 text-center">
          <button
            onClick={() => navigate('/verif', { replace: true })}
            className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium"
          >
            ← Nouvelle vérification
          </button>
        </div>
      </div>
    </div>
  );
}

// Ligne d'info compacte
const Row: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({
  icon, label, children,
}) => (
  <div className="py-2 flex items-start gap-2 text-sm">
    <div className="flex items-center gap-1 text-gray-500 w-24 flex-shrink-0 text-xs uppercase font-semibold pt-0.5">
      {icon} {label}
    </div>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);
